// Error Boundary - Production Error Handling
// Graceful error recovery for the clinical scheduling dashboard

import React, { Component, ReactNode } from 'react';

import type { ErrorBoundaryState, AppError } from '@/types';
import { appConfig, createLogger } from '@/utils/config';

// =============================================================================
// Error Boundary Props
// =============================================================================
interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: AppError) => void;
  showErrorDetails?: boolean;
}

// =============================================================================
// Error Boundary Class Component
// =============================================================================
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private logger: ReturnType<typeof createLogger>;

  constructor(props: ErrorBoundaryProps) {
    super(props);

    this.state = {
      hasError: false,
    };

    this.logger = createLogger('ErrorBoundary');
  }

  // =============================================================================
  // Error Detection
  // =============================================================================
  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    // Update state so the next render will show the fallback UI
    return {
      hasError: true,
      error: {
        code: 'REACT_ERROR_BOUNDARY',
        message: error.message,
        details: {
          name: error.name,
          stack: error.stack,
        },
        timestamp: new Date().toISOString(),
      },
    };
  }

  // =============================================================================
  // Error Logging and Reporting
  // =============================================================================
  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const appError: AppError = {
      code: 'REACT_ERROR_BOUNDARY',
      message: error.message,
      details: {
        name: error.name,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        errorBoundary: this.constructor.name,
      },
      timestamp: new Date().toISOString(),
    };

    // Log the error
    this.logger.error('React Error Boundary caught an error:', {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
    });

    // Report to parent component if callback provided
    if (this.props.onError) {
      this.props.onError(appError);
    }

    // In production, you might want to send this to an error reporting service
    if (process.env.NODE_ENV === 'production') {
      this.reportErrorToService(appError);
    }
  }

  // =============================================================================
  // Error Reporting Service Integration
  // =============================================================================
  private reportErrorToService(error: AppError): void {
    this.logger.error('Unhandled UI error', error);

    // `POST /api/errors` NOW EXISTS (backend api/routes/client_errors.py, added
    // 2026-08-19). Until then this call posted into a 404 for the endpoint's whole
    // life — every caught crash silently discarded while looking like working
    // telemetry (audit R4). Reports land in `fallback_events` under
    // category='client_ui', beside the backend's own degradation events.
    //
    // Deliberately fire-and-forget with a swallowed rejection: an error reporter
    // that can itself throw turns one broken page into two problems. The endpoint
    // always answers 204 for the same reason.
    //
    // NO QUERY STRING IS SENT. window.location.pathname only — a query string is
    // the likeliest place for patient data to ride along, and this is a telemetry
    // table, not a clinical record. The backend strips it again anyway.
    try {
      // AppError keeps the stack inside `details` (see componentDidCatch above),
      // so read it from there rather than from a field the type does not have.
      const details = (typeof error.details === 'object' && error.details
        ? error.details
        : {}) as Record<string, unknown>;
      const body = JSON.stringify({
        message: String(error.message ?? 'unknown').slice(0, 500),
        stack: details.stack ? String(details.stack).slice(0, 4000) : undefined,
        component_stack: details.componentStack
          ? String(details.componentStack).slice(0, 2000)
          : undefined,
        url: window.location.pathname,
        user_agent: navigator.userAgent,
        source: String(details.errorBoundary ?? error.code ?? 'ErrorBoundary'),
        severity: 'HIGH',
      });
      void fetch(`${appConfig.flashBotApi}/api/errors`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body,
        keepalive: true, // survives the navigation a crash often triggers
      }).catch(() => {
        /* reporting must never surface a second failure to the user */
      });
    } catch {
      /* serialising the report failed; the local log above is the fallback */
    }
  }

  // =============================================================================
  // Recovery Functions
  // =============================================================================
  private handleRetry = (): void => {
    this.setState({ hasError: false, error: undefined });
    this.logger.info('Error boundary retry attempted');
  };

  private handleRefresh = (): void => {
    window.location.reload();
  };

  private handleReportIssue = (): void => {
    const { error } = this.state;
    const subject = encodeURIComponent('Clinical Scheduling Dashboard Error');
    const body = encodeURIComponent(
      `Error occurred in Clinical Scheduling Dashboard V3\n\n` +
      `Time: ${error?.timestamp}\n` +
      `Error: ${error?.message}\n` +
      `Code: ${error?.code}\n\n` +
      `Please describe what you were doing when this error occurred:`
    );

    window.open(`mailto:support@delricht.com?subject=${subject}&body=${body}`);
  };

  // =============================================================================
  // Render Error UI
  // =============================================================================
  private renderErrorDetails(): ReactNode {
    const { error } = this.state;
    const { showErrorDetails = process.env.NODE_ENV === 'development' } = this.props;

    if (!showErrorDetails || !error) {
      return null;
    }

    return (
      <div className="mt-4">
        <details className="bg-light border rounded p-3">
          <summary className="fw-medium text-muted cursor-pointer mb-2">
            <i className="bi bi-code-slash me-2"></i>
            Technical Details (Development)
          </summary>

          <div className="small text-monospace">
            <div className="mb-2">
              <strong>Error Code:</strong> {error.code}
            </div>
            <div className="mb-2">
              <strong>Message:</strong> {error.message}
            </div>
            <div className="mb-2">
              <strong>Timestamp:</strong> {error.timestamp}
            </div>

            {error.details && (
              <div className="mt-3">
                <strong>Details:</strong>
                <pre className="bg-dark text-light p-2 rounded mt-1 small overflow-auto">
                  {JSON.stringify(error.details, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </details>
      </div>
    );
  }

  private renderErrorUI(): ReactNode {
    const { error } = this.state;

    return (
      <div className="error-boundary-container d-flex align-items-center justify-content-center min-vh-100">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <div className="card border-danger">
                <div className="card-header bg-danger text-white">
                  <h4 className="card-title mb-0 d-flex align-items-center">
                    <i className="bi bi-exclamation-triangle-fill me-2"></i>
                    Application Error
                  </h4>
                </div>

                <div className="card-body">
                  <div className="text-center mb-4">
                    <i className="bi bi-bug text-danger display-1 mb-3"></i>
                    <h5>Something went wrong with the Clinical Scheduling Dashboard</h5>
                    <p className="text-muted">
                      We encountered an unexpected error while loading the dashboard.
                      This has been logged for our development team.
                    </p>
                  </div>

                  <div className="alert alert-danger mb-4" role="alert">
                    <div className="d-flex align-items-start">
                      <i className="bi bi-exclamation-circle me-2 mt-1"></i>
                      <div>
                        <strong>Error:</strong> {error?.message || 'Unknown error occurred'}
                        <br />
                        <small className="text-muted">
                          Time: {error?.timestamp && new Date(error.timestamp).toLocaleString()}
                        </small>
                      </div>
                    </div>
                  </div>

                  {/* Recovery Actions */}
                  <div className="d-grid gap-2 d-md-flex justify-content-md-center mb-4">
                    <button
                      className="btn btn-primary"
                      onClick={this.handleRetry}
                    >
                      <i className="bi bi-arrow-clockwise me-2"></i>
                      Try Again
                    </button>

                    <button
                      className="btn btn-secondary"
                      onClick={this.handleRefresh}
                    >
                      <i className="bi bi-arrow-repeat me-2"></i>
                      Refresh Page
                    </button>

                    <button
                      className="btn btn-outline-danger"
                      onClick={this.handleReportIssue}
                    >
                      <i className="bi bi-envelope me-2"></i>
                      Report Issue
                    </button>
                  </div>

                  {/* Helpful Information */}
                  <div className="bg-light rounded p-3 mb-3">
                    <h6 className="mb-2">
                      <i className="bi bi-lightbulb me-2"></i>
                      What you can try:
                    </h6>
                    <ul className="mb-0 small">
                      <li>Click "Try Again" to retry loading the dashboard</li>
                      <li>Refresh your browser to start fresh</li>
                      <li>Check your internet connection</li>
                      <li>Try logging out and back in to CRIO</li>
                      <li>Contact IT support if the problem persists</li>
                    </ul>
                  </div>

                  {this.renderErrorDetails()}
                </div>

                <div className="card-footer bg-light text-center">
                  <small className="text-muted">
                    <i className="bi bi-info-circle me-1"></i>
                    Clinical Scheduling Dashboard V3 • DelRicht Research
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // =============================================================================
  // Main Render
  // =============================================================================
  render(): ReactNode {
    if (this.state.hasError) {
      // Use custom fallback if provided, otherwise use default error UI
      return this.props.fallback || this.renderErrorUI();
    }

    return this.props.children;
  }
}

// =============================================================================
// Higher-Order Component for Easy Wrapping
// =============================================================================
export const withErrorBoundary = <P extends object>(
  WrappedComponent: React.ComponentType<P>,
  errorBoundaryProps?: Partial<ErrorBoundaryProps>
) => {
  const WithErrorBoundaryComponent: React.FC<P> = (props) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <WrappedComponent {...props} />
    </ErrorBoundary>
  );

  WithErrorBoundaryComponent.displayName = `withErrorBoundary(${WrappedComponent.displayName || WrappedComponent.name})`;

  return WithErrorBoundaryComponent;
};

// =============================================================================
// Lightweight Error Fallback Component
// =============================================================================
export const ErrorFallback: React.FC<{
  error?: AppError;
  onRetry?: () => void;
  message?: string;
}> = ({ error, onRetry, message = 'Something went wrong' }) => (
  <div className="text-center py-4">
    <i className="bi bi-exclamation-triangle text-warning display-6 mb-3"></i>
    <h5 className="text-muted mb-2">{message}</h5>
    {error?.message && (
      <p className="text-muted small mb-3">{error.message}</p>
    )}
    {onRetry && (
      <button className="btn btn-outline-primary btn-sm" onClick={onRetry}>
        <i className="bi bi-arrow-clockwise me-1"></i>
        Try Again
      </button>
    )}
  </div>
);

export default ErrorBoundary;